import React, { useEffect, useState } from 'react';
import { ReportingAccumulationLocation, ReportingLocation, ReportingTabProps } from 'types/reporting.type';
import LrGrid from 'components/LrGrid/components/Grid';
import { useSelector } from 'react-redux';
import { getTopLevelUserGroup } from 'application/redux/selectors';
import { MenuItem } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Page } from 'components/Layout';
import { Select } from 'components/Input';
import Spacing from 'components/Layout/components/Spacing';
import { groupColumnDefs } from './GridDefs';
import { getIsAdminRole } from 'helpers/roles';
import { fetchReportingAccumulationLocations } from 'services/reporting';

const Accumulations: React.FunctionComponent<ReportingTabProps> = (props) => {
    const { reportName } = props;
    const topGroup = useSelector(getTopLevelUserGroup);
    const isAdmin = getIsAdminRole(topGroup);
    const [reportingLocations, setReportingLocations] = useState<ReportingAccumulationLocation[]>([]);
    const [selectedGroup, setSelectedGroup] = useState('zipcode');
    const [accumulationData, setAccumulationData] = useState<ReportingAccumulationLocation[]>(reportingLocations);
    const [reportingLocationsWithAvg, setReportingLocationsWithAvg] = useState<ReportingAccumulationLocation[]>(
        reportingLocations
    );
    const [regionExpandedReportingLocations, setRegionExpandedReportingLocations] = useState<
        ReportingAccumulationLocation[]
    >(reportingLocations);
    const columnDefs = groupColumnDefs(isAdmin);
    const [selectedGridDefs, setSelectedGridDefs] = useState(columnDefs.defaults);
    const [gridApi, setGridApi] = useState<any>(undefined);

    const sideBar = {
        toolPanels: ['columns', 'filters'],
    };

    const fetchData = async () => {
        const locations = await fetchReportingAccumulationLocations();
        setReportingLocations(locations);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const updateSelections = () => {
        if (selectedGroup === 'zone_group') {
            setAccumulationData(regionExpandedReportingLocations);
        } else {
            setAccumulationData(reportingLocationsWithAvg);
        }
        const newGridDefs = { ...columnDefs.defaults };
        const overrides = columnDefs.overrides[selectedGroup];
        overrides.columnDefs.forEach((def: any, index: number) => {
            newGridDefs.columnDefs[index] = { ...newGridDefs.columnDefs[index], ...def };
        });
        newGridDefs.autoGroupColumnDef = { ...newGridDefs.autoGroupColumnDef, ...overrides.autoGroupColumnDef };
        setSelectedGridDefs(newGridDefs);
        if (gridApi) {
            gridApi.setGridOption('columnDefs', []);
            gridApi.setGridOption('columnDefs', newGridDefs.columnDefs);
            gridApi.setGridOption('autoGroupColumnDef', newGridDefs.autoGroupColumnDef);
            gridApi.sizeColumnsToFit();
        }
    };

    const updateDataCalcs = () => {
        const TIVSum = [0];
        const flattenedLocationsWithGroup = reportingLocations.flatMap((location: ReportingAccumulationLocation) => {
            const tempLoc = { ...location } as ReportingAccumulationLocation;
            if (location.statusStage === 'IN_FORCE') {
                TIVSum[0] += location.totalInsurableValue || 0;
            }
            return location.locationZoneGroups.map((group) => ({
                ...tempLoc,
                locationZoneGroup: group,
            }));
        });

        setReportingLocationsWithAvg(
            reportingLocations
                .filter((location) => ['IN_FORCE', 'QUOTED'].includes(location.statusStage))
                .map((location) => {
                    const tmpLoc = { ...location } as ReportingAccumulationLocation;
                    if (location.statusStage === 'IN_FORCE') {
                        tmpLoc.tivPer = (location?.totalInsurableValue || 0) / TIVSum[0];
                    }
                    return tmpLoc;
                })
        );
        setRegionExpandedReportingLocations(
            flattenedLocationsWithGroup.map(
                (location) =>
                    ({
                        ...location,
                        tivPer: (location?.totalInsurableValue || 0) / TIVSum[0],
                    } as ReportingAccumulationLocation)
            )
        );
    };

    useEffect(() => {
        updateSelections();
    }, [selectedGroup, reportingLocationsWithAvg]);

    useEffect(() => {
        updateDataCalcs();
    }, [reportingLocations]);

    const renderDataGrid = (height: string) => (
        <LrGrid
            gridClassName="ag-theme-balham"
            columnDefs={selectedGridDefs.columnDefs}
            rowData={accumulationData}
            autoGroupColumnDef={selectedGridDefs.autoGroupColumnDef}
            hideStatusBar
            sizeToFit
            maintainColumnOrder
            suppressAggFuncInHeader
            groupTotalRow={'bottom'}
            grandTotalRow={'bottom'}
            setGridApi={setGridApi}
            height={height}
            sideBar={sideBar}
        />
    );

    const handleAccumulationGroupChange = (event: any) => {
        const newSelectedGroup = event.target.value;
        setSelectedGroup(newSelectedGroup);
    };

    return (
        <>
            <Grid xs={10}>
                <Page title={'Accumulation Totals'}>
                    <Grid container spacing={2}>
                        <Grid xs={3}>
                            <Select
                                variant={'standard'}
                                id={'acculation-grouping-select'}
                                label={'Accumulations By'}
                                onChange={handleAccumulationGroupChange}
                                value={selectedGroup}
                            >
                                <MenuItem value={'zipcode'}>Zip Code</MenuItem>
                                <MenuItem value={'state'}>State</MenuItem>
                                <MenuItem value={'zone'}>Zone</MenuItem>
                                <MenuItem value={'zone_group'}>Region</MenuItem>
                            </Select>
                        </Grid>
                        <Grid xs={9} container direction={'column'} spacing={2} alignItems={'flex-start'}>
                            <Grid style={{ width: '100%' }}>{renderDataGrid('700px')}</Grid>
                        </Grid>
                    </Grid>
                    <Spacing height={'18px'} />
                </Page>
            </Grid>
        </>
    );
};

export default Accumulations;
